import { InjectionToken, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import type { HttpRequest } from '@angular/common/http';
import { Subject } from 'rxjs';
import { SUPPRESS_5XX_NOTIFICATION } from './http-error-context.tokens';
import type { HttpErrorFilterFn, HttpErrorReportEvent, NormalizedApiError } from './http-error.models';

export const HTTP_ERROR_FILTERS = new InjectionToken<HttpErrorFilterFn[]>('HTTP_ERROR_FILTERS', {
  factory: () => [],
});

@Injectable({ providedIn: 'root' })
export class HttpErrorReporterService {
  private diFilters = inject(HTTP_ERROR_FILTERS, { optional: true }) ?? [];
  private runtimeFilters: HttpErrorFilterFn[] = [];
  private errorEvents = new Subject<HttpErrorReportEvent>();
  private router = inject(Router);

  public errors$ = this.errorEvents.asObservable();

  public registerFilter(filter: HttpErrorFilterFn): () => void {
    this.runtimeFilters = [...this.runtimeFilters, filter];

    return () => {
      this.runtimeFilters = this.runtimeFilters.filter((item) => item !== filter);
    };
  }

  public report(error: NormalizedApiError, req: HttpRequest<unknown>): HttpErrorReportEvent {
    const filters = [...this.diFilters, ...this.runtimeFilters];
    const suppressed = filters.some((filter) => {
      try {
        return filter(error, req);
      } catch {
        return false;
      }
    });

    const suppressNotification = req.context.get(SUPPRESS_5XX_NOTIFICATION) &&
      error.status >= 500;
    const event: HttpErrorReportEvent = {
      error,
      request: req,
      suppressed,
      suppressNotification,
      timestamp: Date.now(),
      route: this.router?.url ?? undefined,
    };

    this.errorEvents.next(event);

    return event;
  }
}

export const provideHttpErrorFilter = (filter: HttpErrorFilterFn) => ({
  provide: HTTP_ERROR_FILTERS,
  useValue: filter,
  multi: true,
});
